import { watch } from 'vue'
import { useBrandStore } from '@/stores/brand'
import { DEFAULT_WL_TOKENS, mergeTokens } from '@/wl-tokens/defaults'
import { loadEngine } from '@/wl-engines/registry'
import type { WlFontFamily, WlTokens, WlTokensOverride } from '@/wl-tokens/types'

/**
 * WL token injector — stamps the full `WlTokens` set as `--wl-*` CSS custom
 * properties on `<html>` whenever the active pack (or fallback brand) changes.
 *
 * Complements `useBrandThemeEffect`:
 *   - `useBrandThemeEffect` writes the LEGACY 2-color/1-font inputs
 *     (`--brand-theme-color`, `--brand-secondary-color`, `--brand-font-family`)
 *     from the Shop table's flat fields. Kept intact for backwards compat.
 *   - `useWlTokens` (this file) writes the WIDER token surface (`--wl-primary`,
 *     `--wl-ink`, `--wl-font-display`, `--wl-radius-lg`, etc.) from either a
 *     pack manifest OR the same brand-store fallback. This is what future
 *     engine components read directly.
 *
 * Idempotent — safe to install() twice. Only touches DOM on WL hosts.
 *
 * Phase 1: the pack side is stubbed — currently every WL host uses DEFAULT_WL_TOKENS
 * (merged with any legacy `brand.themeColor / secondaryColor / fontFamily`). Phase 2
 * wires up `activeWlPack` from `useBrandStore` when a pack has been uploaded +
 * applied. At that point this composable's output changes but consumers don't.
 */

const FONT_STYLE_ID = 'wl-tokens-fonts'

export function useWlTokens() {
  const store = useBrandStore()

  function install() {
    // Watch both `brand` and `activePack` — either changing triggers re-stamp.
    // A pack apply / preview toggle updates `activePack` without touching
    // `brand`, and we want the CSS vars to flip immediately.
    watch(
      () => ({ brand: store.brand, activePack: store.activePack }),
      async ({ brand, activePack }) => {
        // Off WL — canonical Zaveato host, or a subdomain shop that hasn't
        // opted into full white-label. Leave the DOM untouched so the
        // canonical Zaveato SPA isn't affected.
        if (!brand || !brand.isWhiteLabel) {
          clearTokenVars()
          removeFontStyle()
          return
        }

        // Legacy bridge: build a WlTokensOverride from the shop's flat fields
        // so shops without a pack still get a coherent token set derived from
        // whatever they configured in the old branding form.
        const legacyOverride: WlTokensOverride = {
          colors: {
            ...(brand.themeColor     ? { primary:   brand.themeColor }   : {}),
            ...(brand.secondaryColor ? { secondary: brand.secondaryColor, accent: brand.secondaryColor } : {}),
          },
          fonts: brand.fontFamily
            ? { body: { family: brand.fontFamily, source: 'google', weights: [400, 500, 600, 700] } }
            : undefined,
        }

        // Merge order (later wins over earlier):
        //   1. DEFAULT_WL_TOKENS       — Zaveato defaults, byte-identical to
        //      pre-pack behaviour
        //   2. engine.tokenDefaults    — the engine's opinion of "this is what
        //      my visual grammar looks like" (e.g. board-engine → Bricolage
        //      + DM Mono + tight radii)
        //   3. legacyOverride          — shop's old flat theme fields (bridged
        //      so existing shops don't lose their palette when they pick an
        //      engine but haven't customised the pack)
        //   4. activePack.tokens       — the shop's own pack manifest (final say)
        let tokens = DEFAULT_WL_TOKENS
        try {
          const engine = await loadEngine(activePack?.engineId)
          tokens = mergeTokens(tokens, engine.tokenDefaults)
        } catch (err) {
          console.warn('useWlTokens: engine load failed — using default tokens', err)
        }
        tokens = mergeTokens(tokens, legacyOverride)
        if (activePack?.tokens) {
          tokens = mergeTokens(tokens, activePack.tokens)
        }

        applyTokenVars(tokens)
        applyFontStylesheet(tokens.fonts)
      },
      { immediate: true, deep: true },
    )
  }

  return { install }
}

// ---------------------------------------------------------------------------
// CSS variable stamping
// ---------------------------------------------------------------------------

/**
 * Full list of vars we own on `<html>.style`. Kept in one place so
 * `clearTokenVars()` can remove exactly what we set — never orphaning a var
 * we wrote on a previous pack when the shop switches.
 */
const OWNED_VARS = [
  // colours
  '--wl-primary', '--wl-primary-dark', '--wl-primary-light', '--wl-primary-ink',
  '--wl-secondary', '--wl-secondary-ink',
  '--wl-accent', '--wl-accent-ink',
  '--wl-ink', '--wl-ink-on',
  '--wl-bg', '--wl-bg-alt',
  '--wl-body-text', '--wl-muted-text',
  '--wl-border',
  '--wl-status-open', '--wl-status-closed', '--wl-status-warn',
  // surface tokens (fall back to --wl-ink / --wl-ink-on when omitted)
  '--wl-chrome-bg', '--wl-chrome-ink',
  '--wl-hero-bg', '--wl-hero-ink',
  '--wl-panel-bg', '--wl-panel-ink',
  // fonts
  '--wl-font-display', '--wl-font-body', '--wl-font-mono',
  // shape
  '--wl-radius-sm', '--wl-radius-md', '--wl-radius-lg', '--wl-radius-pill',
  '--wl-border-width', '--wl-mark-radius',
  // motion
  '--wl-motion-fast', '--wl-motion-medium', '--wl-motion-slow', '--wl-motion-ease',
  // assets
  '--wl-logo-url', '--wl-og-image-url',
] as const

function applyTokenVars(t: WlTokens) {
  const s = document.documentElement.style

  // Colours — every slot. Undefined optional slots are computed via
  // `color-mix` in CSS rules that consume them, so we skip them here rather
  // than stamping empty strings.
  s.setProperty('--wl-primary',        t.colors.primary)
  if (t.colors.primaryDark)  s.setProperty('--wl-primary-dark',  t.colors.primaryDark)
  if (t.colors.primaryLight) s.setProperty('--wl-primary-light', t.colors.primaryLight)
  s.setProperty('--wl-primary-ink',    t.colors.primaryInk ?? '#ffffff')

  s.setProperty('--wl-secondary',      t.colors.secondary)
  s.setProperty('--wl-secondary-ink',  t.colors.secondaryInk ?? '#1F2A24')

  s.setProperty('--wl-accent',         t.colors.accent)
  s.setProperty('--wl-accent-ink',     t.colors.accentInk ?? '#1F2A24')

  s.setProperty('--wl-ink',            t.colors.ink)
  s.setProperty('--wl-ink-on',         t.colors.inkOn ?? '#ffffff')

  s.setProperty('--wl-bg',             t.colors.bg)
  s.setProperty('--wl-bg-alt',         t.colors.bgAlt ?? '#ffffff')

  s.setProperty('--wl-body-text',      t.colors.bodyText)
  s.setProperty('--wl-muted-text',     t.colors.mutedText ?? '#6B746F')

  s.setProperty('--wl-border',         t.colors.border)

  if (t.colors.statusOpen)   s.setProperty('--wl-status-open',   t.colors.statusOpen)
  if (t.colors.statusClosed) s.setProperty('--wl-status-closed', t.colors.statusClosed)
  if (t.colors.statusWarn)   s.setProperty('--wl-status-warn',   t.colors.statusWarn)

  // Surface tokens — each pair falls back to --wl-ink / --wl-ink-on when
  // the pack doesn't override, preserving the board-engine's original
  // dark treatment. A pack that wants a fully light chrome sets these
  // to bg / bodyText (or any pair).
  s.setProperty('--wl-chrome-bg',  t.colors.chromeBg  ?? t.colors.ink)
  s.setProperty('--wl-chrome-ink', t.colors.chromeInk ?? t.colors.inkOn ?? '#ffffff')
  s.setProperty('--wl-hero-bg',    t.colors.heroBg    ?? t.colors.ink)
  s.setProperty('--wl-hero-ink',   t.colors.heroInk   ?? t.colors.inkOn ?? '#ffffff')
  s.setProperty('--wl-panel-bg',   t.colors.panelBg   ?? t.colors.ink)
  s.setProperty('--wl-panel-ink',  t.colors.panelInk  ?? t.colors.inkOn ?? '#ffffff')

  // Fonts — the CSS variable holds the family stack; the actual @font-face
  // (or Google Fonts <link>) is injected by `applyFontStylesheet` below.
  s.setProperty('--wl-font-display', fontStack(t.fonts.display, 'serif'))
  s.setProperty('--wl-font-body',    fontStack(t.fonts.body,    'sans-serif'))
  s.setProperty('--wl-font-mono',    fontStack(t.fonts.mono,    'monospace'))

  // Shape
  s.setProperty('--wl-radius-sm',    t.shape.radiusSm)
  s.setProperty('--wl-radius-md',    t.shape.radiusMd)
  s.setProperty('--wl-radius-lg',    t.shape.radiusLg)
  s.setProperty('--wl-radius-pill',  t.shape.radiusPill)
  s.setProperty('--wl-border-width', t.shape.borderWidth)
  s.setProperty('--wl-mark-radius',  t.shape.markRadius)

  // Motion
  s.setProperty('--wl-motion-fast',   t.motion.fast)
  s.setProperty('--wl-motion-medium', t.motion.medium)
  s.setProperty('--wl-motion-slow',   t.motion.slow)
  s.setProperty('--wl-motion-ease',   t.motion.ease)

  // Assets
  if (t.assets.logoUrl)    s.setProperty('--wl-logo-url',     `url("${t.assets.logoUrl}")`)
  if (t.assets.ogImageUrl) s.setProperty('--wl-og-image-url', `url("${t.assets.ogImageUrl}")`)
}

function clearTokenVars() {
  const s = document.documentElement.style
  for (const v of OWNED_VARS) s.removeProperty(v)
}

/** Build a CSS font-family stack string with a safe fallback family. */
function fontStack(f: WlFontFamily, fallback: string): string {
  // Quote family names that contain spaces (Google Fonts convention).
  const quoted = /\s/.test(f.family) ? `"${f.family}"` : f.family
  const chain = [quoted, 'system-ui', '-apple-system', "'Segoe UI'", 'Roboto', fallback].join(', ')
  return chain
}

// ---------------------------------------------------------------------------
// Font loading — one <style> element owns all pack-required @font-face + link
// rules. Cleared + rewritten on every pack change so we never leak fonts
// from a previous pack.
// ---------------------------------------------------------------------------

/** Allowlist of Google Fonts we permit packs to reference by name.
 *  Adding a new family here is a review — keeps supply-chain surface bounded. */
const GOOGLE_FONT_ALLOWLIST = new Set([
  'Inter', 'Poppins', 'Roboto', 'Montserrat', 'Lato', 'Open Sans',
  'Playfair Display', 'Bricolage Grotesque', 'DM Mono', 'DM Sans',
  'Anton', 'Space Mono', 'Space Grotesk', 'Merriweather', 'Nunito',
  'Work Sans', 'IBM Plex Sans', 'IBM Plex Mono',
])

function applyFontStylesheet(fonts: { display: WlFontFamily, body: WlFontFamily, mono: WlFontFamily }) {
  removeFontStyle()

  const parts: string[] = []
  const seenGoogleFamilies = new Set<string>()

  for (const f of [fonts.display, fonts.body, fonts.mono]) {
    if (f.source === 'system') continue

    if (f.source === 'google') {
      if (!GOOGLE_FONT_ALLOWLIST.has(f.family)) {
        console.warn(`[wl-tokens] font family "${f.family}" not in Google Fonts allowlist — skipping load. CSS var still set; will fall back to system stack.`)
        continue
      }
      // Deduplicate — same family may appear across display/body/mono
      if (seenGoogleFamilies.has(f.family)) continue
      seenGoogleFamilies.add(f.family)
      const weights = (f.weights ?? [400, 500, 700]).join(';')
      const familyParam = encodeURIComponent(f.family).replace(/%20/g, '+')
      parts.push(
        `@import url("https://fonts.googleapis.com/css2?family=${familyParam}:wght@${weights}&display=swap");`
      )
      continue
    }

    if (f.source === 'pack') {
      if (!f.packPath) {
        console.warn(`[wl-tokens] pack font "${f.family}" has no packPath — skipping`)
        continue
      }
      // The URL is resolved by the pack's `assetsBaseUrl`, which the
      // backend upload flow signs to point at the shop-scoped S3 prefix.
      // For Phase 1 no pack has been uploaded yet — this branch stays inert
      // until Phase 2 lands the pack loader.
      const src = f.packPath // Phase 2 will prepend the pack's assetsBaseUrl
      const weightAxis = f.variationSettings ?? '400'
      parts.push(
        `@font-face {
          font-family: "${f.family}";
          src: url("${src}") format("woff2");
          font-weight: ${weightAxis};
          font-display: swap;
        }`
      )
      continue
    }
  }

  if (parts.length === 0) return

  const style = document.createElement('style')
  style.id = FONT_STYLE_ID
  style.textContent = parts.join('\n')
  document.head.appendChild(style)
}

function removeFontStyle() {
  document.getElementById(FONT_STYLE_ID)?.remove()
}
